import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, Legend } from "recharts";
import { Users, Building2, Globe2, Timer } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BLUE = "hsl(217 91% 60%)";
const YELLOW = "hsl(43 96% 56%)";
const MUTED = "hsl(215 30% 40%)";
const GRID = "hsl(215 45% 22%)";
const AXIS = "hsl(215 20% 68%)";

const Stat = ({ label, value, icon: Icon, hint, accent }) => (
  <Card className="rounded-none border-border bg-card p-5">
    <div className="flex items-start justify-between">
      <div>
        <div className="label-mono">{label}</div>
        <div className={`font-mono-brand text-4xl mt-2 ${accent ? "text-[hsl(43_96%_60%)]" : ""}`}>{value}</div>
        {hint ? <div className="text-xs text-muted-foreground mt-1">{hint}</div> : null}
      </div>
      <Icon className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
    </div>
  </Card>
);

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [centers, setCenters] = useState([]);
  const [centerId, setCenterId] = useState("__all");

  useEffect(() => {
    api.get("/centers").then(({ data }) => setCenters(data));
  }, []);

  useEffect(() => {
    const params = centerId && centerId !== "__all" ? { center_id: centerId } : {};
    setStats(null);
    api.get("/dashboard/stats", { params }).then(({ data }) => setStats(data));
  }, [centerId]);

  const today = stats?.today || {};
  const trend = stats?.trend_7d?.map(t => ({ ...t, date: t.date.slice(5) })) || [];
  const selectedCenter = centers.find(c => c.id === centerId);

  return (
    <>
      <PageHeader
        eyebrow={user?.role === "super_admin" ? "Organization" : "Your Centers"}
        title="Dashboard"
        subtitle={selectedCenter ? `Scope: ${selectedCenter.name}` : "Real-time attendance across your assigned scope"}
        actions={
          <div className="flex items-center gap-3" data-testid="dashboard-center-filter">
            <span className="label-mono hidden md:inline">Scope</span>
            <Select value={centerId} onValueChange={setCenterId}>
              <SelectTrigger data-testid="dashboard-center-select" className="rounded-none w-52 border-border">
                <SelectValue placeholder="All centers" />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-card">
                <SelectItem value="__all">All centers</SelectItem>
                {centers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        }
      />

      {!stats ? (
        <div className="p-8 grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28 w-full" />)}
        </div>
      ) : (
        <div className="p-8 space-y-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Employees" value={stats.total_employees} icon={Users} hint={`${stats.total_countries} countries`} />
            <Stat label="Centers" value={stats.total_centers} icon={Building2} />
            <Stat label="Present today" value={today.Present || 0} icon={Timer} hint={`${today.Late || 0} late`} accent />
            <Stat label="Absent today" value={today.Absent || 0} icon={Globe2} hint={`${today.Missing_Punch_Out || 0} missing out`} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="rounded-none border-border p-5 lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="label-mono">7-day attendance trend</div>
                  <div className="font-display text-lg">Present vs Late</div>
                </div>
              </div>
              <div className="h-64" data-testid="trend-chart">
                <ResponsiveContainer width="100%" height="100%" minHeight={220}>
                  <LineChart data={trend}>
                    <CartesianGrid stroke={GRID} strokeDasharray="0" vertical={false} />
                    <XAxis dataKey="date" stroke={AXIS} fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke={AXIS} fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ background: "hsl(215 55% 12%)", border: `1px solid ${GRID}`, borderRadius: 0 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line dataKey="present" stroke={BLUE} strokeWidth={2} dot={false} />
                    <Line dataKey="late" stroke={YELLOW} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="rounded-none border-border p-5">
              <div className="label-mono">Today by status</div>
              <div className="font-display text-lg mb-4">Breakdown</div>
              <div className="space-y-3">
                {["Present","Late","Absent","Missing_Punch_Out","Rejected"].map(k => (
                  <div key={k} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{k.replace("_"," ")}</span>
                    <span className="font-mono-brand">{today[k] || 0}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {stats.by_country?.length > 0 && (
              <Card className="rounded-none border-border p-5">
                <div className="label-mono">By country</div>
                <div className="font-display text-lg mb-4">Attendance today</div>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                    <BarChart data={stats.by_country}>
                      <CartesianGrid stroke={GRID} vertical={false} />
                      <XAxis dataKey="country" stroke={AXIS} fontSize={11} tickLine={false} axisLine={false} />
                      <YAxis stroke={AXIS} fontSize={11} tickLine={false} axisLine={false} />
                      <Tooltip contentStyle={{ background: "hsl(215 55% 12%)", border: `1px solid ${GRID}`, borderRadius: 0 }} />
                      <Bar dataKey="employees" fill={MUTED} />
                      <Bar dataKey="present" fill={BLUE} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            )}
            {stats.by_center?.length > 0 && (
              <Card className="rounded-none border-border p-5">
                <div className="label-mono">By center</div>
                <div className="font-display text-lg mb-4">Attendance today</div>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                    <BarChart data={stats.by_center}>
                      <CartesianGrid stroke={GRID} vertical={false} />
                      <XAxis dataKey="center" stroke={AXIS} fontSize={10} tickLine={false} axisLine={false} interval={0} angle={-10} height={40} />
                      <YAxis stroke={AXIS} fontSize={11} tickLine={false} axisLine={false} />
                      <Tooltip contentStyle={{ background: "hsl(215 55% 12%)", border: `1px solid ${GRID}`, borderRadius: 0 }} />
                      <Bar dataKey="present" fill={BLUE} />
                      <Bar dataKey="late" fill={YELLOW} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            )}
          </div>
        </div>
      )}
    </>
  );
}
